import { DEFAULT_CURRENCY } from "@/profitability/currency";
import type {
  ReportBlock,
  ReportControl,
  ReportControlValues,
  ReportDataQuery,
  ReportDataset,
  ReportRow,
  ReportValue,
} from "@/profitability/types";
import { formatReportValue } from "./format-values";

export const AGGREGATE_LABEL = {
  sum: "Total",
  average: "Average",
  count: "Count",
  min: "Lowest",
  max: "Highest",
} as const;

export const reportLink = (value: ReportValue | undefined) =>
  typeof value === "string" && /^https:\/\//.test(value) ? value : null;

export const reportGroups = (
  controls: ReportControl[],
  values: ReportControlValues,
  dataset: string,
) =>
  Array.from(
    new Set(
      controls
        .filter(
          (control) =>
            control.dataset === dataset &&
            control.type === "group" &&
            values[control.id] === true,
        )
        .map((control) => control.field),
    ),
  );
export const blockQuery = (
  block: ReportBlock,
  controls: ReportControl[],
  values: ReportControlValues,
  breakdown?: string,
  /** Narrows the saved result to part of its period; nothing is re-run. */
  lens?: { field: string; start_date: string; end_date: string } | null,
): ReportDataQuery => {
  const groups = reportGroups(controls, values, block.dataset);
  const filters = controls.flatMap((control) => {
    const value = values[control.id];
    if (
      control.dataset !== block.dataset ||
      control.type !== "filter" ||
      value === undefined ||
      (Array.isArray(value) && !value.length)
    )
      return [];
    return [{ field: control.field, operator: control.operator, value }];
  });
  if (lens)
    filters.push(
      { field: lens.field, operator: "gte", value: lens.start_date },
      { field: lens.field, operator: "lte", value: lens.end_date },
    );
  const query: ReportDataQuery = {
    source_id: `report:${block.dataset}`,
    filters,
    group_by: [],
  };
  if (block.type === "table") return query;
  query.group_by = groups.length
    ? groups
    : block.type === "summary"
      ? []
      : [breakdown ?? block.x];
  query.aggregates = [
    {
      field: block.type === "summary" ? block.field : block.y,
      operation: block.aggregate,
      as: "value",
    },
  ];
  // Keep group fields intact even when a script names one of them "value".
  while (query.group_by.includes(query.aggregates[0].as))
    query.aggregates[0].as = `_${query.aggregates[0].as}`;
  return query;
};
type ReportFilter = ReportDataQuery["filters"][number];

/**
 * Strings in the order Ruby's `String#<=>` puts them, which compares UTF-8
 * bytes, and so code points. JS `<` compares UTF-16 code units instead, which
 * puts an emoji before "～": a table filtered here would then keep different
 * rows than its chart, filtered by the API.
 */
const compareStrings = (left: string, right: string) => {
  let i = 0;
  let j = 0;
  while (i < left.length && j < right.length) {
    const a = left.codePointAt(i)!;
    const b = right.codePointAt(j)!;
    if (a !== b) return a < b ? -1 : 1;
    i += a > 0xffff ? 2 : 1;
    j += b > 0xffff ? 2 : 1;
  }
  return Math.sign(left.length - i - (right.length - j));
};

/** Ruby's `<=>`: only like with like compares, and nothing else matches. */
const compare = (value: ReportValue, expected: ReportValue) => {
  if (value === expected) return 0;
  if (typeof value === "number" && typeof expected === "number")
    return value < expected ? -1 : 1;
  if (typeof value === "string" && typeof expected === "string")
    return compareStrings(value, expected);
  return null;
};

const matches = (value: ReportValue, filter: ReportFilter) => {
  const expected = filter.value;
  switch (filter.operator) {
    case "eq":
      return value === expected;
    case "in":
      return Array.isArray(expected) && expected.includes(value);
    case "contains":
      return (
        typeof value === "string" &&
        typeof expected === "string" &&
        value.includes(expected)
      );
    case "gte":
    case "lte": {
      if (Array.isArray(expected)) return false;
      const comparison = compare(value, expected);
      if (comparison === null) return false;
      return filter.operator === "gte" ? comparison >= 0 : comparison <= 0;
    }
  }
};

/**
 * A dataset's rows narrowed by a block's filters, the way the API's
 * `Profitability::DataQuery` narrows them.
 *
 * A table filters here rather than asking the API: its rows already arrived
 * with the report's output, and fetching them again through `/data` meant one
 * sequential request per 64 KB page before the first row could show.
 */
export const filterRows = (rows: ReportRow[], filters: ReportFilter[]) =>
  filters.length
    ? rows.filter((row) =>
        // A field a row leaves out is a blank, as it is in the choices.
        filters.every((filter) => matches(row[filter.field] ?? null, filter)),
      )
    : rows;

/**
 * What each `in` and `eq` filter offers: the distinct values its field takes
 * across the dataset, blanks included.
 */
export const controlChoices = (
  controls: ReportControl[],
  datasets: ReportDataset[],
): Record<string, ReportValue[]> =>
  Object.fromEntries(
    controls
      .filter(
        (control) =>
          control.type === "filter" && ["in", "eq"].includes(control.operator),
      )
      .map((control) => {
        const dataset = datasets.find((item) => item.id === control.dataset)!;
        return [
          control.id,
          Array.from(
            new Set(dataset.rows.map((row) => row[control.field] ?? null)),
          ),
        ];
      }),
  );

/**
 * The filter values that still name one of their control's choices.
 *
 * Values are held above the report, so they can outlive the rows they were
 * picked from: one picked on another run of the period can name a technician
 * this run does not have. Dropping it here, before anything reads the values,
 * keeps the page from being narrowed by a filter its control cannot show. The
 * same object comes back when nothing is dropped.
 */
export const currentValues = (
  values: ReportControlValues,
  choices: Record<string, ReportValue[]>,
): ReportControlValues => {
  let next: ReportControlValues | undefined;
  Object.entries(choices).forEach(([id, options]) => {
    const value = values[id];
    if (value === undefined) return;
    const kept = Array.isArray(value)
      ? value.filter((item) => options.includes(item))
      : options.includes(value)
        ? value
        : undefined;
    const unchanged = Array.isArray(value)
      ? Array.isArray(kept) && kept.length === value.length
      : kept === value;
    if (unchanged) return;
    next ??= { ...values };
    if (kept === undefined || (Array.isArray(kept) && !kept.length))
      delete next[id];
    else next[id] = kept;
  });
  return next ?? values;
};

export const groupParts = (
  row: ReportRow,
  groups: string[],
  dataset: ReportDataset,
  currency: string = DEFAULT_CURRENCY,
) =>
  groups.map((field) =>
    formatReportValue(
      row[field],
      dataset.columns.find((column) => column.key === field)!,
      currency,
    ),
  );

/** One string where a chart label cannot draw a separator. */
export const groupLabel = (
  row: ReportRow,
  groups: string[],
  dataset: ReportDataset,
  currency: string = DEFAULT_CURRENCY,
) => groupParts(row, groups, dataset, currency).join(", ");
/** Group detail rows without assigning new arithmetic rules to table columns. */
export const groupTableRows = (rows: ReportRow[], groups: string[]) => {
  if (!groups.length) return [{ key: "", rows }];
  const result = new Map<string, ReportRow[]>();
  rows.forEach((row) => {
    const key = JSON.stringify(groups.map((field) => row[field] ?? null));
    const group = result.get(key) ?? [];
    group.push(row);
    result.set(key, group);
  });
  return Array.from(result).map(([key, rows]) => ({ key, rows }));
};

export {
  columnLabel,
  measureLabel,
  isBlank,
  formatReportValue,
  formatFigureValue,
  compactReportValue,
} from "./format-values";
